import type { TrackReferenceOrPlaceholder } from '@livekit/components-core'
import { setupFocusToggle, isTrackReferencePinned, isTrackReference } from '@livekit/components-core'

import { useEnsureTrackRef, useMaybeLayoutContext, useRoomContext } from '@/src/context'
import { mergeProps } from '@/src/mergeProps'
import { ButtonHTMLAttributes, useMemo, useCallback } from 'react'
import { useAppSelector } from '@/src/store/hooks'
import { selectIsAdmin } from '@/src/store/slices/meetData'
import { selectRoomState } from '@/src/store/slices/room'
import { selectAccessToken } from '@/src/store/slices/token'

/** @public */
export interface UseFocusToggleProps {
  trackRef?: TrackReferenceOrPlaceholder
  props: ButtonHTMLAttributes<HTMLButtonElement>
}

/**
 * The `useFocusToggle` hook is used to implement the `FocusToggle` or your custom implementation of it.
 * The `TrackReferenceOrPlaceholder` is used to register a onClick handler and to identify the track to focus on.
 *
 * @example
 * ```tsx
 * const { mergedProps, inFocus } = useFocusToggle({ trackRef, props: yourButtonProps });
 * return <button {...mergedProps}>{inFocus ? 'Unfocus' : 'Focus'}</button>;
 * ```
 * @public
 */
export function useFocusToggle({ trackRef, props }: UseFocusToggleProps) {
  const trackReference = useEnsureTrackRef(trackRef)

  const layoutContext = useMaybeLayoutContext()
  const room = useRoomContext()
  const isAdmin = useAppSelector(selectIsAdmin)
  const { livekit_url } = useAppSelector(selectRoomState)
  const accessToken = useAppSelector(selectAccessToken)
  const { className } = useMemo(() => setupFocusToggle(), [])

  const inFocus = useMemo(
    () => isTrackReferencePinned(trackReference, layoutContext?.pin.state),
    [trackReference, layoutContext?.pin.state],
  )

  const handleClick = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      props.onClick?.(event)

      if (inFocus) {
        layoutContext?.pin.dispatch?.({
          msg: 'clear_pin',
        })
      } else {
        layoutContext?.pin.dispatch?.({
          msg: 'set_pin',
          trackReference,
        })
      }

      try {
        const metadata = JSON.parse(room.metadata || '{}')
        
        if (metadata.focusLockEnabled === true && isAdmin && livekit_url && room) {
          let newFocus = null
          
          if (!inFocus) {
            if (isTrackReference(trackReference)) {
              newFocus = {
                participantIdentity: trackReference.participant.identity,
                trackSource: trackReference.publication.source,
              }
            } else if (trackReference && 'participant' in trackReference && 'source' in trackReference) {
              newFocus = {
                participantIdentity: trackReference.participant.identity,
                trackSource: trackReference.source,
              }
            }
          }

          const newMetadata = {
            ...metadata,
            focusLockEnabled: true,
            lockedFocus: newFocus,
          }

          const bodyReq = {
            roomName: room.name,
            metadata: newMetadata,
          }

          await fetch(
            `${window.origin}/api/update_metadata?livekit_url=${livekit_url}`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'text/plain',
                authorization: accessToken || '',
              },
              body: JSON.stringify(bodyReq),
            }
          )
        }
      } catch (error) {
        console.error('Error updating focus lock metadata:', error)
      }
    },
    [props, inFocus, layoutContext?.pin, trackReference, room, isAdmin, livekit_url, accessToken],
  )

  const mergedProps = useMemo(
    () =>
      mergeProps(props, {
        className,
        onClick: handleClick,
      }),
    [props, className, handleClick],
  )

  return { mergedProps, inFocus }
}
